import { useEffect, useState } from 'react';
import { FileText, Loader2, MapPin, Phone, User, X } from 'lucide-react';
import api from '../services/api';

/**
 * Formats a MAXPAR calendar value without shifting the day across time zones.
 */
function formatDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/**
 * Formats a CPF or CNPJ returned by MAXPAR.
 */
function formatDocument(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '—';
}

/**
 * Joins the insured phone numbers as (DDD) number.
 */
function formatPhones(phones) {
  if (!Array.isArray(phones) || phones.length === 0) return '—';
  return phones
    .map((phone) => {
      const ddd = phone?.Ddd || '';
      const number = phone?.Numero || '';
      if (!ddd && !number) return '';
      return ddd ? `(${ddd}) ${number}` : number;
    })
    .filter(Boolean)
    .join(', ') || '—';
}

/**
 * Picks the active MAXPAR configuration used to read one policy.
 */
function queryConfig(configs) {
  const maxpar = (configs || []).filter((config) => String(config.api_owner || '').toLowerCase() === 'maxpar');
  return maxpar.find((config) => String(config.method || '').toUpperCase() === 'GET') || maxpar[0] || null;
}

/**
 * Reads the first policy object from the MAXPAR query payload.
 */
function firstPolicy(payload) {
  if (Array.isArray(payload)) return payload[0] || null;
  if (payload && typeof payload === 'object') return payload;
  return null;
}

/**
 * Loads the policy from MAXPAR and shows it in a right-hand panel.
 */
function MaxparPolicyDrawer({ customer, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    if (!customer?.policyNumber) return undefined;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      setPolicy(null);
      try {
        const configs = await api.get('/api-configs/active/list');
        const config = queryConfig(configs.data?.configs);
        if (!config) {
          throw new Error('Não há uma consulta MAXPAR ativa configurada.');
        }
        const response = await api.get(`/upload/maxpar/policy/${encodeURIComponent(customer.policyNumber)}`, {
          params: { api_config_id: config.id }
        });
        const found = firstPolicy(response.data?.data);
        if (!active) return;
        if (!found) {
          setError('A MAXPAR não devolveu dados para esta apólice.');
          return;
        }
        setPolicy(found);
      } catch (requestError) {
        if (!active) return;
        setError(requestError.response?.data?.error || requestError.message || 'Erro ao consultar a apólice na MAXPAR');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [customer]);

  useEffect(() => {
    if (!customer) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [customer, onClose]);

  if (!customer) return null;

  const address = policy?.Segurado?.Endereco;
  const cancelled = Boolean(policy?.DataCancelamento);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fechar consulta"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="maxpar-drawer-title"
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Consulta MAXPAR</p>
            <h2 id="maxpar-drawer-title" className="text-lg font-semibold text-gray-900">
              {customer.customerName || 'Cliente sem nome'}
            </h2>
            <p className="text-sm text-gray-600">Apólice {customer.policyNumber}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && (
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Consultando a apólice na MAXPAR...
            </p>
          )}

          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          {policy && (
            <>
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Apólice
                </h3>
                <Detail label="Número" value={policy.Apolice || customer.policyNumber} />
                <Detail label="Produto" value={policy.Produto} />
                <Detail label="Parceiro" value={policy.Parceiro} />
                <Detail label="Início da vigência" value={formatDate(policy.DataInicioVigencia)} />
                <Detail label="Fim da vigência" value={formatDate(policy.DataFimVigencia)} />
                <Detail
                  label="Cancelamento"
                  value={cancelled ? formatDate(policy.DataCancelamento) : 'Sem cancelamento na MAXPAR'}
                />
              </section>

              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <User className="h-4 w-4 text-gray-500" />
                  Segurado
                </h3>
                <Detail label="Nome" value={policy.Segurado?.Nome} />
                <Detail label="CPF/CNPJ" value={formatDocument(policy.Segurado?.Documento)} />
                <Detail label="Tipo" value={policy.Segurado?.TipoPessoa} />
                <Detail label="E-mail" value={policy.Segurado?.Email} />
                <Detail label="Nascimento" value={formatDate(policy.Segurado?.DataNascimento)} />
                <div>
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="flex items-center gap-1 text-sm font-medium text-gray-900">
                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                    {formatPhones(policy.Segurado?.Telefones)}
                  </p>
                </div>
              </section>

              {address && (
                <section className="space-y-2">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    Endereço
                  </h3>
                  <Detail
                    label="Logradouro"
                    value={[address.Logradouro, address.Numero, address.Complemento]
                      .map((part) => String(part || '').replace(/^[\s,]+|[\s,]+$/g, ''))
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <Detail label="Bairro" value={address.Bairro} />
                  <Detail label="Cidade" value={[address.Cidade, address.Estado].filter(Boolean).join(' / ')} />
                  <Detail label="CEP" value={address.Cep} />
                </section>
              )}

              {Array.isArray(policy.Planos) && policy.Planos.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-800">Planos</h3>
                  {policy.Planos.map((plan, index) => (
                    <div key={`${plan.Codigo || 'plano'}-${index}`} className="rounded-lg border border-gray-200 p-3 space-y-1">
                      <Detail label="Código" value={plan.Codigo} />
                      <Detail label="Início" value={formatDate(plan.DataInicioVigencia)} />
                      <Detail label="Fim" value={formatDate(plan.DataFimVigencia)} />
                    </div>
                  ))}
                </section>
              )}

              {policy.Veiculo && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-800">Veículo</h3>
                  <Detail label="Placa" value={policy.Veiculo.placa || policy.Veiculo.Placa} />
                  <Detail label="Chassi" value={policy.Veiculo.chassi || policy.Veiculo.Chassi} />
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * Renders one labeled value inside the policy panel.
 */
function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  );
}

export default MaxparPolicyDrawer;
